// i18n (ADR 0012) : catalogues générés depuis packages/i18n. Français chargé d'emblée ; mooré et dioula (lot F17d,
// ADR 0084) chargés à la demande quand la langue du compte est proposée par l'API, le français servant de repli.
// Les catalogues des pages (« access », lot F1 ; « referentials », « status » et « platform », lot F2 ; « projects » et « me », lot F3 ; « budget », lot F6) sont chargés à part, juste après le démarrage, pour tenir
// le budget JS initial (≤ 200 Ko gzip) : les pages qui les utilisent sont elles-mêmes chargées à la demande.
import i18n, { type BackendModule, type ReadCallback } from 'i18next';
import { initReactI18next } from 'react-i18next';
import type academyCatalog from '@wangre/i18n/i18next/fr/academy.json';
import type accessCatalog from '@wangre/i18n/i18next/fr/access.json';
import type activitiesCatalog from '@wangre/i18n/i18next/fr/activities.json';
import type alertsCatalog from '@wangre/i18n/i18next/fr/alerts.json';
import type associationCatalog from '@wangre/i18n/i18next/fr/association.json';
import type analyticsCatalog from '@wangre/i18n/i18next/fr/analytics.json';
import type budgetCatalog from '@wangre/i18n/i18next/fr/budget.json';
import type commsCatalog from '@wangre/i18n/i18next/fr/comms.json';
import type dashboardsCatalog from '@wangre/i18n/i18next/fr/dashboards.json';
import type fieldCatalog from '@wangre/i18n/i18next/fr/field.json';
import type financeCatalog from '@wangre/i18n/i18next/fr/finance.json';
import type hrCatalog from '@wangre/i18n/i18next/fr/hr.json';
import type integrationsCatalog from '@wangre/i18n/i18next/fr/integrations.json';
import type knowledgeCatalog from '@wangre/i18n/i18next/fr/knowledge.json';
import type platformCatalog from '@wangre/i18n/i18next/fr/platform.json';
import type projectsCatalog from '@wangre/i18n/i18next/fr/projects.json';
import type referentialsCatalog from '@wangre/i18n/i18next/fr/referentials.json';
import type reportsCatalog from '@wangre/i18n/i18next/fr/reports.json';
import type statusCatalog from '@wangre/i18n/i18next/fr/status.json';
import common from '@wangre/i18n/i18next/fr/common.json';
import errors from '@wangre/i18n/i18next/fr/errors.json';
import type meCatalog from '@wangre/i18n/i18next/fr/me.json';
import nav from '@wangre/i18n/i18next/fr/nav.json';
import states from '@wangre/i18n/i18next/fr/states.json';

export const NAMESPACES = ['common', 'nav', 'status', 'states', 'errors', 'platform', 'me', 'access', 'referentials', 'projects', 'activities', 'field', 'budget', 'dashboards', 'alerts', 'reports', 'analytics', 'comms', 'hr', 'finance', 'association', 'academy', 'knowledge', 'integrations'] as const;

const bundled = { common, nav, states, errors };

export const resources = { fr: bundled } as const;

/** Catalogues typés (clés vérifiées à la compilation), y compris ceux chargés à part. */
export type Catalogs = typeof bundled & {
  me: typeof meCatalog;
  access: typeof accessCatalog;
  referentials: typeof referentialsCatalog;
  status: typeof statusCatalog;
  platform: typeof platformCatalog;
  projects: typeof projectsCatalog;
  activities: typeof activitiesCatalog;
  field: typeof fieldCatalog;
  budget: typeof budgetCatalog;
  dashboards: typeof dashboardsCatalog;
  alerts: typeof alertsCatalog;
  reports: typeof reportsCatalog;
  analytics: typeof analyticsCatalog;
  comms: typeof commsCatalog;
  hr: typeof hrCatalog;
  finance: typeof financeCatalog;
  association: typeof associationCatalog;
  academy: typeof academyCatalog;
  knowledge: typeof knowledgeCatalog;
  integrations: typeof integrationsCatalog;
};

const LAZY: Record<string, () => Promise<{ default: object }>> = {
  access: () => import('@wangre/i18n/i18next/fr/access.json'),
  referentials: () => import('@wangre/i18n/i18next/fr/referentials.json'),
  // Libellés des statuts : utilisés par les pages (chargées à la demande), pas par la coquille (lot F2, budget).
  status: () => import('@wangre/i18n/i18next/fr/status.json'),
  platform: () => import('@wangre/i18n/i18next/fr/platform.json'),
  projects: () => import('@wangre/i18n/i18next/fr/projects.json'),
  activities: () => import('@wangre/i18n/i18next/fr/activities.json'),
  field: () => import('@wangre/i18n/i18next/fr/field.json'),
  budget: () => import('@wangre/i18n/i18next/fr/budget.json'),
  // Accueils, tableaux de bord, notifications et recherche (lot F7a).
  dashboards: () => import('@wangre/i18n/i18next/fr/dashboards.json'),
  alerts: () => import('@wangre/i18n/i18next/fr/alerts.json'),
  // Rapports et analytique (lot F7b).
  reports: () => import('@wangre/i18n/i18next/fr/reports.json'),
  analytics: () => import('@wangre/i18n/i18next/fr/analytics.json'),
  // Messagerie et annonces (lot F8).
  comms: () => import('@wangre/i18n/i18next/fr/comms.json'),
  // Ressources humaines (lot F10a).
  hr: () => import('@wangre/i18n/i18next/fr/hr.json'),
  // Comptabilité générale (lot F11a).
  finance: () => import('@wangre/i18n/i18next/fr/finance.json'),
  association: () => import('@wangre/i18n/i18next/fr/association.json'),
  // Académie (lot F14).
  academy: () => import('@wangre/i18n/i18next/fr/academy.json'),
  // Capitalisation (lot F15).
  knowledge: () => import('@wangre/i18n/i18next/fr/knowledge.json'),
  // Intégrations (lot F17).
  integrations: () => import('@wangre/i18n/i18next/fr/integrations.json'),
  // Espace « Moi » : pages chargées à la demande (lot F3, budget).
  me: () => import('@wangre/i18n/i18next/fr/me.json'),
};

/** Mooré et dioula : un fichier par espace, présent dès qu'un libellé est traduit (import des traducteurs). */
const TRANSLATIONS = import.meta.glob<{ default: object }>('../../../../packages/i18n/dist/i18next/{mos,dyu}/*.json');

/** Chargeur d'un espace dans une langue (absent : aucun libellé traduit, le français s'affiche). */
export function catalogLoader(language: string, namespace: string): (() => Promise<{ default: object }>) | undefined {
  return language === 'fr' ? LAZY[namespace] : TRANSLATIONS[`../../../../packages/i18n/dist/i18next/${language}/${namespace}.json`];
}

const lazyCatalogs: BackendModule = {
  type: 'backend',
  init: () => undefined,
  read: (language: string, namespace: string, callback: ReadCallback) => {
    const load = catalogLoader(language, namespace);
    if (!load) {
      callback(null, {});
      return;
    }
    load().then(
      (module) => callback(null, module.default as Record<string, unknown>),
      (error: unknown) => callback(error as Error, false),
    );
  },
};

void i18n
  .use(lazyCatalogs)
  .use(initReactI18next)
  .init({
    resources,
    partialBundledLanguages: true,
    lng: 'fr',
    fallbackLng: 'fr',
    ns: NAMESPACES,
    defaultNS: 'common',
    interpolation: { escapeValue: false },
    returnNull: false,
  });

/** Langue du compte (GET /me, déjà ramenée au français par l'API si elle n'est pas proposée). */
export function applyLocale(locale: string | undefined): void {
  const next = locale === 'mos' || locale === 'dyu' ? locale : 'fr';
  document.documentElement.lang = next;
  if (i18n.language !== next) void i18n.changeLanguage(next);
}

export default i18n;
